import { useState } from "react";

function makeReadableDocName(path) {
  if (!path) return "Document";
  const url = typeof path === "string" ? path : path?.url || path?.name || "";
  if (!url) return "Document";
  let name = decodeURIComponent(String(url).split("/").pop() || "Document");
  name = name.replace(/-\d+\.(pdf|docx|txt|csv|png|jpe?g|gif|webp)$/i, ".$1");
  name = name.replace(/(\.[a-z0-9]+)\1$/i, "$1");
  name = name.replace(/[_-]+/g, " ");
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** Normalize docs from change (relatedDocs) or refund (documents). */
export function normalizeRelatedDocs(order) {
  const raw = [
    ...(Array.isArray(order?.relatedDocs) ? order.relatedDocs : []),
    ...(Array.isArray(order?.relatedDocuments) ? order.relatedDocuments : []),
    ...(Array.isArray(order?.documents) ? order.documents : []),
  ];
  if (!raw.length) return [];
  const seen = new Set();
  return raw
    .map((doc) => {
      if (typeof doc === "string")
        return { url: doc, name: makeReadableDocName(doc) };
      if (doc && typeof doc === "object") {
        const url = doc.url || doc.path || doc.href || "";
        if (!url) return null;
        return {
          url,
          name: doc.name || doc.fileName || makeReadableDocName(url),
        };
      }
      return null;
    })
    .filter((doc) => {
      if (!doc?.url || seen.has(doc.url)) return false;
      seen.add(doc.url);
      return true;
    });
}

async function downloadDoc(doc) {
  const res = await fetch(doc.url, { mode: "cors" });
  if (!res.ok) throw new Error("fetch failed");
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = doc.name || "document";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Agent-uploaded proof on a change / details-change request.
 * View opens the file; Download tries a file save (falls back to open).
 */
export default function RelatedDocsPanel({
  data,
  title = "Supporting documents",
  empty = false,
}) {
  const [busyUrl, setBusyUrl] = useState("");
  const docs = normalizeRelatedDocs(data);

  if (!docs.length && !empty) return null;

  async function handleDownload(doc) {
    if (!doc?.url || busyUrl) return;
    setBusyUrl(doc.url);
    try {
      await downloadDoc(doc);
    } catch (_) {
      window.open(doc.url, "_blank", "noopener,noreferrer");
    } finally {
      setBusyUrl("");
    }
  }

  return (
    <div className="shrink-0 rounded-xl border border-[#E8EEF5] bg-white p-3">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-[#0F172A]">{title}</h3>
        {docs.length ? (
          <span className="text-[11px] text-[#6D7C94]">
            {docs.length} file{docs.length === 1 ? "" : "s"} from the agent
          </span>
        ) : null}
      </div>
      {!docs.length ? (
        <p className="text-[12px] text-[#94A3B8]">No documents attached.</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {docs.map((doc, index) => (
            <div
              key={`${doc.url}-${index}`}
              className="flex items-center justify-between gap-2 rounded-lg border border-[#E8EEF5] bg-[#F8FAFC] px-2.5 py-1.5"
            >
              <span
                className="text-[13px] text-[#0F172A] truncate min-w-0"
                title={doc.name}
              >
                {doc.name}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[12px] font-semibold text-primary1 hover:underline"
                >
                  View
                </a>
                <button
                  type="button"
                  disabled={busyUrl === doc.url}
                  onClick={() => handleDownload(doc)}
                  className="text-[12px] font-semibold text-primary1 underline hover:opacity-80 disabled:opacity-50"
                >
                  {busyUrl === doc.url ? "…" : "Download"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
